import Tooltip from "@/ui/Tooltip"
import type { Placement } from "@floating-ui/react"
import type { RefObject } from "react"
import { motion } from "motion/react"
import { useHudContext } from "../hud/shared/playbookHUDInfra"
import { METRIC_LABEL_BASELINE_TRIM_CLASS, useMetricHoverSwap } from "../hud/blocks/metrics/metricHoverSwap"
import { useMetricHoverTrigger } from "../hud/blocks/hoverSwapStickyZone"
import { useHoverSwapsDisabled } from "../hud/blocks/hoverSwapsDisabled"
import {
  MetricDefinitionPanel,
  useUnifiedHudPanels,
} from "../hud/blocks/panels/unifiedHoverPanels"
import { MetricTrigger } from "../hud/metric-details/MetricTrigger"
import { METRIC_DETAILS_HINT } from "../hud/metric-details/metricLabels"
import { TickerNumber } from "./TickerNumber"

const ALIGNMENT_RESEARCHER_BUTTON_CLASS_NAME = [
  "text-[12px] leading-none w-4 h-4 bg-black",
  "flex items-center justify-center rounded-xs text-white hover:opacity-100",
].join(" ")

// Round to a single significant figure (e.g. 2205 -> 2000, 3771 -> 4000): the
// safety-researcher count is a rough estimate, so a precise-looking "3,771"
// overstates its precision. Shown compact after this (2000 -> "2K").
const roundToOneSignificantFigure = (value: number): number => {
  if (value <= 0) return 0
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
  return Math.round(value / magnitude) * magnitude
}

const ALIGNMENT_RESEARCHERS_TOOLTIP = <div>
  <p className="mb-2 font-bold font-mono uppercase"> Safety Researchers</p>
  <p>
    Estimated researchers focused primarily on technical alignment work with
    access to frontier models, compute, and relevant implementation resources.
  </p>
  <p className="mt-2 border-t border-black/10 pt-1.5 font-sans text-[10px] text-[var(--race-background)]">{METRIC_DETAILS_HINT}</p>
</div>

export const AlignmentResearcherCount = ({
  interactive = false,
  doublings = 0,
  currentYear,
  onIncrement,
  onDecrement,
  oneLine = false,
  valueFirst = false,
  label = "Safety Researchers",
  units = " Researchers",
  labelFontSize = "10.5px",
  tooltipPlacement = "top-start",
  tooltipPositionRef,
}: {
  interactive?: boolean
  doublings?: number
  currentYear?: number
  onIncrement?: () => void
  onDecrement?: () => void
  oneLine?: boolean
  valueFirst?: boolean
  label?: string
  units?: string
  labelFontSize?: string
  tooltipPlacement?: Placement
  tooltipPositionRef?: RefObject<HTMLElement | null>
}) => {
  const ctx = useHudContext()
  const safetyResearcherPopulation =
    ctx.currentPhase?.hudMetrics?.safetyResearcherPopulation ?? 0
  const displayedResearchers = roundToOneSignificantFigure(
    safetyResearcherPopulation * Math.pow(2, doublings)
  )
  const buttonsVisible = currentYear === undefined || currentYear >= 2028
  // Inside a <MetricHoverSwap> provider the floating tooltip is suppressed:
  // hover is reported to the swap context instead, and a MetricHoverSwapTarget
  // elsewhere on the HUD renders this metric's definition in-canvas. The
  // MetricTrigger (click → details panel) stays either way.
  const metricSwap = useMetricHoverSwap()
  const hoverSwapsDisabled = useHoverSwapsDisabled()
  // Red-chip treatment (tab 3): the stat row stretches its items so the four
  // hover chips share one height — the wrapper below stretches the trigger to
  // the row's full band instead of centering it at natural height.
  const chipRow = metricSwap?.triggerHighlight === "red-chip"
  const unifiedPanels = useUnifiedHudPanels()
  // Mirrors the TickerNumber rendering below (compact notation + units).
  const metricHoverProps = useMetricHoverTrigger(
    "alignmentEffort",
    `${new Intl.NumberFormat("en-US", { notation: "compact", maximumSignificantDigits: 2 }).format(displayedResearchers)}${units}`,
  )
  const trigger = (
    <MetricTrigger
      id="alignment"
      // self-stretch/px-1.5 belong to the production stats row; the px-1.5
      // is its tooltip hover surface. The swap row goes flush (the padding
      // would indent the figure off the policy column's edge), and hover-less
      // surfaces (the mobile dashboard) drop the padding too — no tooltips
      // there, and its narrow stat columns need the width.
      className={valueFirst
        ? metricSwap || hoverSwapsDisabled
          ? "flex flex-col-reverse justify-end items-center w-min"
          : "flex flex-col-reverse justify-end items-center w-min self-stretch px-1.5"
        : oneLine ? "flex w-full items-baseline justify-between gap-1.5" : "relative top-[1px]"}
      {...metricHoverProps}
    >
      <div className={`${valueFirst ? "text-center" : "whitespace-nowrap"} ${valueFirst && metricSwap ? METRIC_LABEL_BASELINE_TRIM_CLASS : ""}`} style={{ fontSize: labelFontSize }}>
        {label}
      </div>
      <div className={`text-[11px] font-bold whitespace-nowrap ${valueFirst ? "" : oneLine ? "" : "relative top-0.5"}`}>
        {/* 2 significant digits, not fixed decimals: a forecast rendered as
            "225.2K" implies far more precision than the model has, while the
            low early-year counts still need a fractional digit ("1.5K"). */}
        <TickerNumber
          value={displayedResearchers}
          suffix={units}
          format={{ notation: "compact", maximumSignificantDigits: 2 }}
        />
      </div>
    </MetricTrigger>
  )

  return (
    <div className={`flex ${valueFirst && !interactive && !metricSwap ? "items-stretch self-stretch" : chipRow ? "items-stretch" : "items-center"}`}>
      {interactive && (
        <motion.div
          initial={false}
          animate={{
            width: buttonsVisible ? "auto" : 0,
            opacity: buttonsVisible ? 1 : 0,
          }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className={`overflow-hidden flex flex-col items-center gap-1 ${buttonsVisible ? "mr-1.5" : ""}`}
        >
          <Tooltip
            placement="top-start"
            maxWidth={200}
            content={<div className="p-2">
              Simulate doubling the number of alignment researchers.
            </div>}
          >
            <button
              type="button"
              onClick={onIncrement}
              className={ALIGNMENT_RESEARCHER_BUTTON_CLASS_NAME}
              aria-label="Double alignment researchers"
            >
              +
            </button>
          </Tooltip>
          <Tooltip
            placement="top-start"
            maxWidth={200}
            content={<div className="p-2">
              Simulate halving the number of alignment researchers.
            </div>}
          >
            <button
              type="button"
              onClick={onDecrement}
              className={ALIGNMENT_RESEARCHER_BUTTON_CLASS_NAME}
              aria-label="Halve alignment researchers"
            >
              −
            </button>
          </Tooltip>
        </motion.div>
      )}
      {/* Hover-less surfaces (the mobile dashboard): no definition tooltip —
          a tap must go straight to the MetricTrigger's click (the details
          page) instead of surfacing the hover affordance. */}
      {metricSwap || hoverSwapsDisabled ? trigger : (
        <Tooltip
          placement={tooltipPlacement}
          positionRef={tooltipPositionRef}
          disableShadow={Boolean(unifiedPanels)}
          content={unifiedPanels ? (
            <MetricDefinitionPanel metricId="alignmentEffort" />
          ) : (
            <div className="p-2 w-[280px]">{ALIGNMENT_RESEARCHERS_TOOLTIP}</div>
          )}
        >
          {trigger}
        </Tooltip>
      )}
    </div>
  )
}
